import { Bold, Italic, Underline, List, ListOrdered } from 'lucide-react'
import { ToolbarActionDivider, ToolbarIconButton } from '@/entities/node/ui'
import type { TextNodeBlockType, TextNodeListType } from '@/shared/model/text-style-types'
import { cn } from '@/shared/lib/utils'

const TEXT_BLOCK_TYPE_OPTIONS: Array<{ value: TextNodeBlockType; label: string; shortLabel: string }> = [
    { value: 'paragraph', label: '正文', shortLabel: 'P' },
    { value: 'h1', label: '标题 1', shortLabel: 'H1' },
    { value: 'h2', label: '标题 2', shortLabel: 'H2' },
    { value: 'h3', label: '标题 3', shortLabel: 'H3' },
]

export function ToolbarToggleButton({
    active,
    children,
    onClick,
    title,
}: {
    active?: boolean
    children: React.ReactNode
    onClick: () => void
    title: string
}) {
    return (
        <ToolbarIconButton
            onClick={onClick}
            title={title}
            className={cn(
                'min-w-7 px-2 font-medium',
                active && 'border border-white/14 bg-white/10 text-white hover:bg-white/12',
            )}
        >
            {children}
        </ToolbarIconButton>
    )
}

export interface TextFormattingToolbarProps {
    textStyle: {
        blockType?: TextNodeBlockType
        bold?: boolean
        italic?: boolean
        underline?: boolean
        listType?: TextNodeListType
    }
    setBlockType: (type: TextNodeBlockType) => void
    toggleTextDecoration: (decoration: 'bold' | 'italic' | 'underline') => void
    toggleListType: (type: 'bullet' | 'ordered') => void
}

export function TextFormattingToolbar({
    textStyle,
    setBlockType,
    toggleTextDecoration,
    toggleListType,
}: TextFormattingToolbarProps) {
    return (
        <>
            {TEXT_BLOCK_TYPE_OPTIONS.map((option) => (
                <ToolbarToggleButton
                    key={option.value}
                    active={textStyle.blockType === option.value}
                    onClick={() => setBlockType(option.value)}
                    title={option.label}
                >
                    <span className="text-[11px] tracking-[0.04em]">{option.shortLabel}</span>
                </ToolbarToggleButton>
            ))}

            <ToolbarActionDivider />

            <ToolbarToggleButton
                active={textStyle.bold}
                onClick={() => toggleTextDecoration('bold')}
                title="粗体"
            >
                <Bold className="h-3.5 w-3.5" />
            </ToolbarToggleButton>
            <ToolbarToggleButton
                active={textStyle.italic}
                onClick={() => toggleTextDecoration('italic')}
                title="斜体"
            >
                <Italic className="h-3.5 w-3.5" />
            </ToolbarToggleButton>
            <ToolbarToggleButton
                active={textStyle.underline}
                onClick={() => toggleTextDecoration('underline')}
                title="下划线"
            >
                <Underline className="h-3.5 w-3.5" />
            </ToolbarToggleButton>

            <ToolbarActionDivider />

            <ToolbarToggleButton
                active={textStyle.listType === 'bullet'}
                onClick={() => toggleListType('bullet')}
                title="无序列表"
            >
                <List className="h-3.5 w-3.5" />
            </ToolbarToggleButton>
            <ToolbarToggleButton
                active={textStyle.listType === 'ordered'}
                onClick={() => toggleListType('ordered')}
                title="有序列表"
            >
                <ListOrdered className="h-3.5 w-3.5" />
            </ToolbarToggleButton>
        </>
    )
}
